import { History } from 'history';
import { useHistory } from 'react-router-dom';
import { isEmpty } from 'utils/arrays';
import { getScrollStorage, updateScrollStorage, Scroll } from 'utils/storage';
import { useSessionStorage } from './useSessionStorage';

const sessionStorageKey = 'routing';

export type Parameter = {
  path: History.Path;
  state?: History.LocationState;
  height?: number;
};

export type State = {
  index: number;
  routeList: Parameter[];
  isNormalRouting: boolean;
};

const initialState: State = {
  index: -1,
  routeList: [],
  isNormalRouting: true,
};

function createState(value: string | null): State {
  if (value === null) {
    return initialState;
  }
  return JSON.parse(value) || initialState;
}

// popStateと混同しそうなのでpopRoutingは定義しない
// 実質的にforwardを使用できなくなる
export function useRouting(): {
  getRouting: () => State;
  setRouting: (value: State) => void;
  getFirstRoute: () => Parameter;
  setFirstRoute: (value: Parameter, isHistory: boolean) => void;
  finishNormalRouting: () => void;
  pushRouting: (value: Parameter, isHistory: boolean, height?: number) => void;
  replaceRouting: (
    value: Parameter,
    isHistory: boolean,
    current?: string,
    height?: number
  ) => void;
  backRouting: (isHistory: boolean, count?: number) => void;
} {
  const { getSessionValue, setSessionValue } = useSessionStorage(
    sessionStorageKey
  );
  const history = useHistory();

  function initializeRoutingStorage() {
    setRouting(initialState);
  }

  function getRouting(): State {
    return createState(getSessionValue());
  }

  function setRouting(value: State) {
    setSessionValue(JSON.stringify(value));
  }

  function getFirstRoute() {
    const route = getRouting();
    const firstRoute = route.routeList[0] || {
      path: '/home',
    };
    return firstRoute;
  }

  function setFirstRoute(value: Parameter, isHistory: boolean) {
    initializeRoutingStorage();
    pushRouting(value, isHistory);
  }

  function finishNormalRouting() {
    const state = getRouting();
    state.isNormalRouting = false;
    setRouting(state);
  }

  function pushRouting(value: Parameter, isHistory: boolean, height = 0) {
    const state = getRouting();
    if ((state.routeList[state.index]?.path ?? '') !== value.path) {
      // スクロール
      const scrollStorage = getScrollStorage();
      const routeItem =
        state.routeList.length === 0
          ? null
          : state.routeList[state.routeList.length - 1];

      if (routeItem !== null && height !== 0) {
        if (scrollStorage === null) {
          const list = [{ path: routeItem.path, height }] as Scroll[];
          updateScrollStorage(list);
        } else {
          const list = [...scrollStorage, { path: routeItem.path, height }];
          updateScrollStorage(list);
        }
      }

      state.routeList.push(value);
      state.index++;
      setRouting(state);
      if (isHistory) {
        history.push(value.path, value.state);
      }
    }
  }

  function replaceRouting(
    value: Parameter,
    isHistory: boolean,
    current?: string,
    height?: number
  ) {
    const state = getRouting();
    state.routeList.pop();
    state.index--;

    if ((state.routeList[state.index]?.path ?? '') !== value.path) {
      if (current !== undefined && height !== undefined) {
        // スクロール
        const scrollStorage = getScrollStorage();
        if (current !== null && height !== 0) {
          if (scrollStorage === null) {
            const list = [{ path: current, height }] as Scroll[];
            updateScrollStorage(list);
          } else {
            const list = [
              ...scrollStorage,
              { path: current, height },
            ] as Scroll[];
            updateScrollStorage(list);
          }
        }
      }

      state.routeList.push(value);
      state.index++;
    }
    setRouting(state);
    if (isHistory) {
      history.replace(value.path, value.state);
    }
  }

  function backRouting(isHistory: boolean, count = 1) {
    const state = getRouting();
    for (let i = 0; i < count; i++) {
      state.routeList.pop();
      state.index--;
    }

    // そもそも戻る対象がない場合
    if (isEmpty(state.routeList)) {
      setFirstRoute({ path: '/home' }, true);
      return;
    }

    setRouting(state);
    if (isHistory) {
      if (state.isNormalRouting) {
        history.go(-count);
      } else {
        // goBackは使用しない
        setRouting(state);
        const route = state.routeList[state.index];
        history.push(route.path, route.state);
      }
    }
  }

  return {
    getRouting,
    setRouting,
    getFirstRoute,
    setFirstRoute,
    finishNormalRouting,
    pushRouting,
    replaceRouting,
    backRouting,
  };
}
